import React, { createContext, useContext, type ReactNode } from "react";
import { StandardProps, StandardComponent } from "@/components/componentMap";

export type StandardPropsContextValue = StandardProps & { sizes: string };

const StandardPropsContext = createContext<StandardPropsContextValue | null>(
  null,
);

export function StandardPropsProvider({
  children,
  ...value
}: StandardPropsContextValue & { children: ReactNode }) {
  return (
    <StandardPropsContext.Provider value={value}>
      {children}
    </StandardPropsContext.Provider>
  );
}

export function useStandardProps(): StandardPropsContextValue {
  const ctx = useContext(StandardPropsContext);
  if (!ctx)
    throw new Error("useStandardProps must be used within a standardComponent");
  return ctx;
}

/**
 * Creates a component that receives StandardProps via context.
 *
 * @example
 * export const MyComponent = standardComponent("MyComponent", () => {
 *   const { product, flags } = useStandardProps();
 *   return <div>{product.title}</div>;
 * });
 */
export function standardComponent(
  displayName: string,
  Render: () => ReactNode,
): StandardComponent {
  const wrapper: StandardComponent = (props) => (
    <StandardPropsContext.Provider value={props}>
      <Render />
    </StandardPropsContext.Provider>
  );
  wrapper.displayName = displayName;
  return wrapper;
}
